// src/pages/profile/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import PageLayout from '../../components/base/PageLayout';
import TwoFactorSetup from './components/TwoFactorSetup';
import { useNavigate } from 'react-router-dom';

const ProfilePage = () => {
  const { user, userProfile, updateUserProfile, loading } = useAuthContext();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    timezone: 'Eastern Time (ET)'
  });
  
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailNotifications: true
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Load user data when component mounts or userProfile changes
  useEffect(() => {
    if (userProfile) {
      setFormData({
        fullName: userProfile.fullName || '',
        email: userProfile.email || '',
        phoneNumber: userProfile.phoneNumber || '',
        timezone: userProfile.timezone || 'Eastern Time (ET)'
      });
      
      if (userProfile.notificationPreferences) {
        setNotificationPreferences(userProfile.notificationPreferences);
      }
    }
  }, [userProfile]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationToggle = (type) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Removed advanced notification types. Only emailNotifications remains.

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setMessage('');
    
    try {
      await updateUserProfile({
        ...formData,
        notificationPreferences: {
          emailNotifications: !!notificationPreferences.emailNotifications
        }
      });
      
      // Send notification
      await notify.profileUpdated();
      
      setMessage('Changes saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('Error saving changes. Please try again.');
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto p-6">
        {/* Personal Information Section */}
        <div className="bg-gray-900 rounded-lg p-6 mb-6">
          <h2 className="text-white text-xl font-semibold mb-6">Personal Information</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-gray-400 text-sm block mb-2">Full Name</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label className="text-gray-400 text-sm block mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
                placeholder="your@email.com"
                disabled
              />
            </div>
            
            <div>
              <label className="text-gray-400 text-sm block mb-2">Phone Number</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
                placeholder="+1 (555) 123-4567"
              />
          </div>

            <div>
              <label className="text-gray-400 text-sm block mb-2">Timezone</label>
              <select
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
              >
                <option value="Eastern Time (ET)">Eastern Time (ET)</option>
                <option value="Central Time (CT)">Central Time (CT)</option>
                <option value="Mountain Time (MT)">Mountain Time (MT)</option>
                <option value="Pacific Time (PT)">Pacific Time (PT)</option>
              </select>
          </div>
        </div>

          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="mt-6 bg-purple-500 text-white rounded-lg px-6 py-2 hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          
          {message && (
            <p className={`mt-3 text-sm ${message.includes('Error') ? 'text-red-500' : 'text-green-500'}`}>
              {message}
            </p>
          )}
        </div>

        {/* Security Section (2FA) */}
        <TwoFactorSetup />

        {/* Notification Preferences Section */}
        <div className="bg-gray-900 rounded-lg p-6">
          <h2 className="text-white text-xl font-semibold mb-6">Notification Preferences</h2>
          
          {/* Notification Toggles */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium">Email Notifications</h3>
                <p className="text-gray-400 text-sm">Receive updates via email</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={notificationPreferences.emailNotifications}
                  onChange={() => handleNotificationToggle('emailNotifications')}
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default ProfilePage;
